/**
 * BJELLA I TOPPEN — det ENESTE stedet man får varsler (276.10, Atle 24. oktober).
 *
 * «Kontraktvarsler kommer i den samme varselbjella i headeren. Det skal ikke finnes flere steder å
 * få varsler i Tvitvi.» Innboks › Varsler og panelet «Uleste varsler» på oversikten er derfor borte,
 * og det de to hadde som bjella ikke hadde, er flyttet hit: ikonet per type, «merk som lest» per rad
 * uten å gå dit, de leste som historikk, og en hel side framfor åtte rader.
 *
 * =================================================================================================
 * EN POPOVER OG IKKE EN MENY LENGER.
 *
 * Bjella var en `DropdownMenu` så lenge hver rad var ett valg. Nå har en rad både en lenke og en
 * lesekontroll, og panelet har et valg mellom «Uleste» og «Leste» — knapper inni et menypunkt er
 * ikke en meny, og menyens tastaturfelle spiser dem. Lenka i setningen dekker hele raden
 * (`stretch`), så et klikk på raden går fortsatt til objektet og merker den lest.
 *
 * =================================================================================================
 * MERKET SIER «MER ENN», IKKE «NØYAKTIG», NÅR DET ER DET SANNE SVARET.
 *
 * `unread-count` er en begrenset aggregering: den stopper på `UNREAD_BADGE_MAX` og sier fra med
 * `capped`. «99+» og «99» betyr to forskjellige ting, og klienten kan ikke finne på forskjellen
 * selv — den kommer fra ruten.
 */
import * as RadixPopover from '@radix-ui/react-popover';
import type { MeResponse, NotificationDto } from '@tvitvi/shared';
import { useInfiniteQuery, useQuery } from '@tanstack/react-query';
import { Link, useNavigate } from '@tanstack/react-router';
import { useEffect, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import { meInOrganizationQueryOptions } from '@/api/queries/me';
import { actorFromMe, allows } from '@/auth/actor';
import { bookingSideOf } from '@/auth/organization-modules';
import { ErrorState } from '@/components/state/ErrorState';
import { Button } from '@/components/ui/Button';
import { IconButton } from '@/components/ui/IconButton';
import { ICONS, iconFor } from '@/components/ui/icons';
import { stopPortalClick } from '@/components/ui/portal-events';
import { Segmented } from '@/components/ui/Segmented';
import { usePortalTheme } from '@/components/ui/use-portal-theme';
import {
  unreadCountQueryOptions,
  unreadNotificationsQueryOptions,
  useMarkAllNotificationsRead,
  useMarkNotificationRead,
} from '@/features/overview/queries';
import { useGuestMode } from '@/features/guest/guest-mode';
import { cn } from '@/lib/cn';
import { formatDateTime, formatNumber } from '@/lib/format';

import { subscribeBellOpen } from './bell-signal';
import { notificationIcon } from './notification-icon';
import { NotificationSentence, notificationFeedResource } from './NotificationSentence';
import { readNotificationsInfiniteQueryOptions } from './queries';
import { useClearNotificationsOnArrival } from './use-clear-on-arrival';
import { useNotificationRoute } from './use-notification-route';

/**
 * En hel side uleste — det varselfanen hentet. Bjella er nå det eneste stedet, så den viser alle
 * framfor et utvalg med en lenke til resten. Lista ruller inni panelet.
 */
export const BELL_ROWS = 50;

type BellView = 'unread' | 'read';

/**
 * `me` KOMMER SOM EN PROP OG IKKE FRA `useMe()`.
 *
 * Toppen får den slik allerede, og `AppShell.test.tsx` tegner skallet med en stubbet `me` uten en
 * spørring bak. Et `useMe()` her gjorde fire av de testene røde med «Cannot read properties of
 * undefined (reading 'locale')» — og det ville vært like galt i produktet: to lesere av den samme
 * raden, der den ene kan være tom mens den andre ikke er det.
 */
export function NotificationBell({
  me,
  organizationId,
}: {
  me: MeResponse;
  organizationId: string;
}): ReactNode {
  const { t } = useTranslation();
  /* Aktøren utledes av den SAMME `me` toppen fikk — se hodet for hvorfor ikke `useActor()`. */
  const actor = actorFromMe(me);
  const portal = usePortalTheme();
  const [open, setOpen] = useState(false);
  const [view, setView] = useState<BellView>('unread');

  const openBell = (next: boolean): void => {
    if (next) portal.capture();
    else setView('unread');
    setOpen(next);
  };

  /* Toasten om en nevning og gamle `?view=notifications`-lenker ber bjella åpne seg. */
  const { capture } = portal;
  useEffect(
    () =>
      subscribeBellOpen(() => {
        capture();
        setView('unread');
        setOpen(true);
      }),
    [capture],
  );

  const badge = useQuery(unreadCountQueryOptions(organizationId));

  const markAllRead = useMarkAllNotificationsRead(organizationId);
  /* Punkt 111: står leseren på stedet et ulest varsel peker til, er det lest — alle typer. */
  useClearNotificationsOnArrival(organizationId, bookingSideOf(me), me.activeOrgRole !== 'guest');

  const guest = me.activeOrgRole === 'guest';

  /*
   * I GJESTEMODUS SPØRS HJEMMETS AKTØR (P11). Gjesteaktøren har ikke `notification:update` — og skal
   * ikke ha det — men varslene og «Merk alle som lest» går til hjemmet, og det er rollen og cellene
   * DER som avgjør. De står ikke på `me` (den er gjestens), så `/me` spørs med hjemmet i headeren,
   * bare når panelet er åpent.
   *
   * DEN SAMME RESSURSEN SOM FØR: en RAD, ikke en collection — se `notificationFeedResource`.
   */
  const home = useQuery({
    ...meInOrganizationQueryOptions(organizationId),
    enabled: guest && open,
  });
  const readActor = guest ? (home.data === undefined ? null : actorFromMe(home.data)) : actor;
  const canMarkRead =
    readActor !== null &&
    allows(readActor, 'notification:update', notificationFeedResource(readActor));

  const count = badge.data?.count ?? 0;
  const Bell = ICONS.bell;

  return (
    <RadixPopover.Root open={open} onOpenChange={openBell}>
      <RadixPopover.Trigger asChild>
        <button
          type="button"
          ref={portal.anchorRef}
          aria-label={t('notifications.bell.label')}
          data-testid="notification-bell"
          /*
           * SOM HVER ANNEN IKONKNAPP (Glass 2, §16.5 punkt 1–2): grått ikon, rund, ingen kant, tonal
           * hover. Den sto med kant og flate fra A1 (fanget av buttons-look-like-buttons 18. september);
           * G6 tok kanten, og ikonet er det som gjør den til en handling (tolkning 6).
           */
          className={cn(
            'hit-target relative flex size-8 items-center justify-center rounded-full',
            'border border-transparent bg-transparent text-muted ring-focus',
            'hover:bg-[var(--fill-tonal)] hover:text-default',
          )}
        >
          <Bell aria-hidden className="size-4" />
          {count > 0 ? (
            <span
              data-testid="notification-bell-count"
              className="numeric absolute -top-0.5 -right-0.5 min-w-4 rounded-full bg-accent-strong px-1 text-2xs leading-4 font-medium text-on-accent"
            >
              {badge.data?.capped === true
                ? `${formatNumber(count, me.user.locale)}+`
                : formatNumber(count, me.user.locale)}
            </span>
          ) : null}
        </button>
      </RadixPopover.Trigger>

      <RadixPopover.Portal>
        <RadixPopover.Content
          {...portal.attributes}
          onClick={stopPortalClick}
          align="end"
          sideOffset={6}
          collisionPadding={8}
          aria-label={t('notifications.bell.title')}
          data-testid="notification-bell-panel"
          className="glass-popover z-50 flex max-h-[min(36rem,calc(100vh-5rem))] w-[24rem] max-w-[calc(100vw-1rem)] flex-col"
        >
          <div className="flex items-center justify-between gap-2 px-3 pt-3 pb-2">
            <h2 className="text-sm font-semibold text-default">{t('notifications.bell.title')}</h2>
            <Segmented
              label={t('notifications.bell.label')}
              value={view}
              onChange={setView}
              options={[
                { value: 'unread', label: t('notifications.bell.unread') },
                { value: 'read', label: t('notifications.bell.read') },
              ]}
              testId="notification-bell-view"
            />
          </div>

          <div className="min-h-0 flex-1 overflow-y-auto">
            {view === 'unread' ? (
              <UnreadRows
                me={me}
                organizationId={organizationId}
                canMarkRead={canMarkRead}
                badge={badge.data}
                close={() => openBell(false)}
              />
            ) : (
              <ReadRows me={me} organizationId={organizationId} close={() => openBell(false)} />
            )}
          </div>

          <div className="flex items-center justify-between gap-2 border-t border-subtle px-3 py-2">
            {/* F51: varslingsvalgene — personens egne, i alle husene, så ingen vei ut av gjestemodus. */}
            <Link
              to="/profile/notifications"
              data-testid="notification-bell-preferences"
              className="ring-focus rounded-xs text-xs text-muted hover:text-default hover:underline"
              onClick={() => openBell(false)}
            >
              {t('notifications.bell.preferences')}
            </Link>
            {view === 'unread' && count > 0 && canMarkRead ? (
              <Button
                size="sm"
                variant="quiet"
                data-testid="notification-bell-mark-all"
                loading={markAllRead.isPending}
                onClick={() => markAllRead.mutate()}
              >
                {t('notifications.bell.markAllRead')}
              </Button>
            ) : null}
          </div>
        </RadixPopover.Content>
      </RadixPopover.Portal>
    </RadixPopover.Root>
  );
}

/** De uleste. Hentes bare når panelet er åpent — komponenten finnes ikke ellers. */
function UnreadRows({
  me,
  organizationId,
  canMarkRead,
  badge,
  close,
}: {
  me: MeResponse;
  organizationId: string;
  canMarkRead: boolean;
  /** Merket: tallet og om aggregeringen stoppet på taket. */
  badge: { count: number; capped: boolean } | undefined;
  close: () => void;
}): ReactNode {
  const { t } = useTranslation();
  const list = useQuery(unreadNotificationsQueryOptions(organizationId, BELL_ROWS));
  const markRead = useMarkNotificationRead(organizationId);
  const rows = list.data?.items ?? [];
  /*
   * DET SOM IKKE FIKK PLASS, fra oversiktspanelet: av aggregeringen, ikke av `hasMore`. Stoppet
   * tellingen på taket, sies ingenting — «99+» og «49 varsler til» ved femti rader ville vært to
   * påstander om det samme tallet som ikke stemmer med hverandre.
   */
  const hidden = badge === undefined || badge.capped ? 0 : Math.max(badge.count - rows.length, 0);

  if (list.isError) {
    return (
      <div className="px-3 py-3" data-testid="notification-bell-error">
        <ErrorState inline error={list.error} onRetry={() => void list.refetch()} />
      </div>
    );
  }
  if (rows.length === 0) {
    return (
      <p className="px-3 py-3 text-xs text-subtle" data-testid="notification-bell-empty">
        {list.isPending ? t('common.loading') : t('notifications.bell.empty')}
      </p>
    );
  }

  return (
    <ol className="flex flex-col">
      {rows.map((row) => (
        <BellRow
          key={row.id}
          row={row}
          me={me}
          testId={`notification-bell-row-${row.id}`}
          onFollow={() => {
            markRead.mutate(row.id);
            close();
          }}
          action={
            canMarkRead ? (
              <IconButton
                icon="check"
                label={t('notifications.bell.markRead')}
                data-testid={`notification-bell-read-${row.id}`}
                className="relative z-10"
                onClick={() => markRead.mutate(row.id)}
              />
            ) : null
          }
          meta={formatDateTime(row.createdAt, me.user.locale)}
        />
      ))}
      {hidden > 0 ? (
        <li className="numeric px-3 py-2 text-xs text-subtle" data-testid="notification-bell-more">
          {t('notifications.bell.more', { count: hidden })}
        </li>
      ) : null}
    </ol>
  );
}

/** F45: historikken — det som er lest, nyeste først, med når det ble lest. */
function ReadRows({
  me,
  organizationId,
  close,
}: {
  me: MeResponse;
  organizationId: string;
  close: () => void;
}): ReactNode {
  const { t } = useTranslation();
  const list = useInfiniteQuery(readNotificationsInfiniteQueryOptions(organizationId));
  const rows: NotificationDto[] = list.data?.pages.flatMap((page) => page.items) ?? [];

  if (list.isError) {
    return (
      <div className="px-3 py-3" data-testid="notification-bell-error">
        <ErrorState inline error={list.error} onRetry={() => void list.refetch()} />
      </div>
    );
  }
  if (rows.length === 0) {
    return (
      <p className="px-3 py-3 text-xs text-subtle" data-testid="notification-bell-history-empty">
        {list.isPending ? t('common.loading') : t('notifications.bell.readEmpty')}
      </p>
    );
  }

  return (
    <div data-testid="notification-bell-history">
      <ol className="flex flex-col">
        {rows.map((row) => (
          <BellRow
            key={row.id}
            row={row}
            me={me}
            muted
            testId={`notification-bell-history-row-${row.id}`}
            onFollow={close}
            meta={
              row.readAt === null
                ? formatDateTime(row.createdAt, me.user.locale)
                : t('notifications.bell.readAt', {
                    when: formatDateTime(row.readAt, me.user.locale),
                  })
            }
          />
        ))}
      </ol>
      {list.hasNextPage ? (
        <div className="px-3 py-2">
          <Button
            size="sm"
            variant="quiet"
            loading={list.isFetchingNextPage}
            onClick={() => void list.fetchNextPage()}
            data-testid="notification-bell-history-more"
          >
            {t('notifications.bell.loadMore')}
          </Button>
        </div>
      ) : null}
    </div>
  );
}

/**
 * Én rad: ikonet for typen, setningen med grunnen under, tidspunktet og eventuelt en kontroll.
 *
 * LENKEN I SETNINGEN SLUKTE KLIKKET i menyutgaven (K1 F5). Her finnes ingen menyvalg å sluke:
 * lenka er hele raden, og klikket på den merker lest og lukker panelet selv.
 */
function BellRow({
  row,
  me,
  testId,
  onFollow,
  meta,
  action = null,
  muted = false,
}: {
  row: NotificationDto;
  me: MeResponse;
  testId: string;
  onFollow: () => void;
  meta: string;
  action?: ReactNode;
  muted?: boolean;
}): ReactNode {
  /* Hvilken side av bookingen varselet åpnes på: modulene, ikke typen (246.6). */
  const kind = bookingSideOf(me);
  const target = useNotificationRoute(row, kind);
  const navigate = useNavigate();
  /*
   * I GJESTEMODUS ER VARSLENE HJEMMETS (P10, funn C): det de peker på, ligger i personens egen
   * organisasjon. Gjestemodus forlates først, som «Tilbake» i gjestelinja — ellers åpnes målet med
   * organisasjonen som delte i headeren.
   */
  const { leave } = useGuestMode();
  const guest = me.activeOrgRole === 'guest';
  /* Typen, ikke nøkkelen — se `notification-icon.ts`. */
  const icon = notificationIcon(row.type);
  const TypeIcon = iconFor(icon);
  return (
    <li
      data-testid={testId}
      className="relative flex items-start gap-2.5 border-b border-subtle px-3 py-2 last:border-b-0 hover:bg-[var(--fill-tonal)]"
    >
      <TypeIcon
        aria-hidden
        data-testid={`notification-bell-icon-${row.id}`}
        data-icon={icon}
        className="mt-0.5 size-4 shrink-0 text-subtle"
      />
      <span className="flex min-w-0 flex-1 flex-col gap-0.5">
        <span className={cn('text-xs', muted ? 'text-muted' : 'text-default')}>
          <NotificationSentence
            notification={row}
            kind={kind}
            stretch
            onNavigate={(event) => {
              onFollow();
              if (!guest || target === null) return;
              event.preventDefault();
              void leave().then(() => navigate(target));
            }}
          />
        </span>
        <span className="numeric text-2xs text-subtle">{meta}</span>
      </span>
      {action}
    </li>
  );
}
